/**
 * DocumentFilters — spec iter183 (2026-02-27)
 *
 * Barre de filtres extraite de `pages/Documents.jsx` (Point 1b du backlog
 * technique). Contient :
 *   1. Champ de recherche global (avec debounce, shortcut hint, clear btn).
 *   2. Puces récapitulatives des filtres de colonne actifs
 *      (`ActiveColumnFilterChips`).
 *   3. Sélecteurs Statut + Catégorie.
 *   4. Toggle « Grouper par catégorie ».
 *   5. Bloc « Filtres rapides » (3 groupes séparés par des filets
 *      verticaux : QuickChip G1, PeriodePicker + LocationChipDropdown,
 *      QuickChip G3 + chip « Étapes à revalider »).
 *
 * Contrainte : zéro changement visuel. Les composants locaux
 * (ActiveColumnFilterChips, QuickChip, PeriodePicker, LocationChipDropdown)
 * ainsi que les constantes (NONE, QUICK_G1, QUICK_G3, DOC_STATUS_MAP)
 * restent définis dans le parent et sont passés en props.
 */
import { Fragment, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, AlertTriangle, Layers, ChevronDown, CalendarRange, MapPin, MapPinOff, X, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { J3Groups, PANEL } from "@/components/common/ProjectJ3";
import SearchClearButton from "@/components/ui/SearchClearButton";
import SearchDebounceBar from "@/components/ui/SearchDebounceBar";
import SearchShortcutHint from "@/components/ui/SearchShortcutHint";
// Spec iter185 (2026-02-27) — DOCS_KPIS ré-exporté depuis DocumentHeader
// (utilisé par ActiveColumnFilterChips pour libellés « Statut = X »).
import { DOCS_KPIS } from "@/components/documents/DocumentHeader";

// Spec iter184 (2026-02-27) — constantes de libellés déplacées depuis
// Documents.jsx. Elles ne sont utilisées que par ActiveColumnFilterChips
// (déplacé dans ce fichier lors de la sous-étape 1b bis) ; leur place est
// donc ici pour préserver la cohésion.
const COL_FILTER_LABELS = {
  status:      "Statut",
  type:        "Type",
  project:     "Siège/Projet",
  creator:     "Créé par",
  resp:        "Resp.",
  proEtat:     "Pro. étape",
  progress:    "Progression",
  attachments: "Pièces jointes",
};
const PRO_ETAT_LABEL = {
  retard:   "Délai dépassé",
  ouvert:   "Ouvert",
  en_cours: "En cours",
  termine:  "Terminé",
  cloture:  "Clôturé",
};
const PROGRESS_LABEL = {
  not_started: "Non commencé",
  in_progress: "En cours",
  done:        "Terminé",
  out_of_order: "Ordre incohérent",
};
const ATTACH_LABEL = {
  with:    "Avec",
  without: "Sans",
};

export default function DocumentFilters({
  // Constantes/dictionnaires
  NONE, DOC_STATUS_MAP, QUICK_G1, QUICK_G3, typeCategories,
  // Recherche
  searchInputRef, search, setSearch, searchFocused, setSearchFocused, searching = false,
  debouncedSearch,
  // Filtres primaires (Select)
  statusFilter, setStatusFilter,
  categoryFilter, setCategoryFilter,
  scope, setScope,
  // Grouper par catégorie
  groupByCategory, setGroupByCategory,
  // Chips de colonnes
  colFilters, setColFilters, getChipCount,
  typesById, projectsById, usersById,
  // Période
  period, setPeriod,
  // Projet
  projectFilter, setProjectFilter,
  projectStats, projects,
  // Liste courante (pour chip « Étapes à revalider »)
  items,
}) {
  return (
      <Card className="p-3 bg-card border-vertec-borderDark space-y-3" data-testid="docs-filters">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[280px]">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={searchInputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              placeholder="Référence, titre, contenu, créateur…"
              className="w-full rounded-md focus:outline-none focus:ring-1 focus:ring-vertec-blue"
              style={{
                height: 32,
                paddingLeft: 30, paddingRight: 28,
                background: "#232326",
                border: "1px solid #2A2A2A",
                color: "#EDEDED",
                fontSize: 12,
              }}
              data-testid="docs-search"
            />
            {searching && <Loader2 size={12} className="absolute animate-spin text-[#8b8b93]" style={{ right: 30, top: 10 }} data-testid="docs-search-spinner" />}
            <SearchClearButton value={search} onClear={() => setSearch("")} testid="docs-search-clear" />
            <SearchShortcutHint
              visible={!searchFocused && !search}
              testid="docs-search-shortcut-hint"
            />
            {/* Spec rev162 (2026-02) — liseré animé pendant les 250 ms de
                debounce. Bleu #0078D4 pour Documents. */}
            <SearchDebounceBar
              active={search !== debouncedSearch}
              color="#0078D4"
              testid="docs-search-debounce-bar"
            />
          </div>
          {/* Spec rev119 (2026-02) — Puces récapitulatives des filtres de
              colonne actifs, insérées juste à droite de la barre de
              recherche. Chaque puce affiche « [Colonne] : [valeur] » +
              une croix cliquable qui retire le filtre concerné sans
              passer par le menu de la colonne. Fond bleu #0A2540, texte
              #6FA0E0, pill radius, cohérent avec les autres puces. */}
          <ActiveColumnFilterChips
            colFilters={colFilters}
            setColFilters={setColFilters}
            typesById={typesById}
            projectsById={projectsById}
            usersById={usersById}
            getChipCount={getChipCount}
          />
          <Select value={statusFilter || NONE} onValueChange={(v) => { setStatusFilter(v === NONE ? "" : v); setScope(""); }}>
            <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="docs-status-filter">
              <SelectValue placeholder="Tous les statuts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Tous les statuts</SelectItem>
              {Object.entries(DOC_STATUS_MAP).filter(([k]) => k !== "terminee").map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Spec rev75 (2026-08-02) — filtre par catégorie transversale de type. */}
          <Select value={categoryFilter || NONE} onValueChange={(v) => setCategoryFilter(v === NONE ? "" : v)}>
            <SelectTrigger className="w-[200px] h-8 text-xs" data-testid="docs-category-filter">
              <SelectValue placeholder="Toutes les catégories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Toutes les catégories</SelectItem>
              {typeCategories.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block w-2 h-2 rounded-full" style={{ background: c.color }} />
                    {c.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Spec rev76 (2026-08-02) — toggle vue « Groupée par catégorie ». */}
          <button
            type="button"
            onClick={() => setGroupByCategory((v) => !v)}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs transition-colors"
            style={{
              background: groupByCategory ? "rgba(90,158,224,0.14)" : "#232326",
              border: `1px solid ${groupByCategory ? "rgba(90,158,224,0.55)" : "#2A2A2A"}`,
              color: groupByCategory ? "#5A9EE0" : "#9CA3AF",
            }}
            title="Regrouper les documents par catégorie de type"
            data-testid="docs-group-by-cat-toggle"
          >
            <Layers size={12} />
            Grouper par catégorie
          </button>
        </div>

        {/* Filtres rapides — 3 groupes séparés par un filet vertical ---- */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2">
            Filtres rapides
          </div>
          <div className="flex items-stretch gap-2" data-testid="docs-quick-chips">
            {/* Groupe 1 */}
            <div className="flex items-center gap-2 flex-1 justify-around">
              {QUICK_G1.map((c) => (
                <QuickChip key={c.v} chip={c} active={scope === c.v}
                  onClick={() => { setScope(scope === c.v ? "" : c.v); setStatusFilter(""); }} />
              ))}
            </div>
            <span aria-hidden="true" className="shrink-0"
              style={{ width: 1, alignSelf: "stretch", background: "#2a2a28", margin: "4px 0" }} />
            {/* Groupe 2 */}
            <div className="flex items-center gap-2 flex-1 justify-around">
              <PeriodePicker value={period} onChange={setPeriod} />
              <LocationChipDropdown
                projects={projectStats.projects}
                unassigned={projectStats.unassigned}
                value={projectFilter}
                onChange={setProjectFilter}
                nameById={Object.fromEntries((projects || []).map((p) => [p.id, p.name]))}
              />
            </div>
            <span aria-hidden="true" className="shrink-0"
              style={{ width: 1, alignSelf: "stretch", background: "#2a2a28", margin: "4px 0" }} />
            {/* Groupe 3 */}
            <div className="flex items-center gap-2 flex-1 justify-around">
              {QUICK_G3.map((c) => (
                <QuickChip key={c.v} chip={c} active={scope === c.v}
                  onClick={() => { setScope(scope === c.v ? "" : c.v); setStatusFilter(""); }} />
              ))}
            </div>
          </div>
        </div>
      </Card>
  );
}

// ---------------------------------------------------------------
// Helpers locaux — spec iter184 (2026-02-27)
// Déplacés depuis `pages/Documents.jsx` (Point 1b sub-étape). Ces 4
// composants ne sont utilisés QUE par DocumentFilters ; les déplacer ici
// enlève ~410 lignes de Documents.jsx sans changer un pixel à l'écran.
// ---------------------------------------------------------------
function ActiveColumnFilterChips({ colFilters, setColFilters, typesById, projectsById, usersById, getChipCount }) {
  const chips = [];
  const removeKey = (k) => setColFilters((prev) => {
    const n = { ...prev }; delete n[k]; return n;
  });
  const removeArrValue = (k, v) => setColFilters((prev) => {
    const n = { ...prev };
    const cur = Array.isArray(prev[k]) ? prev[k].filter((x) => x !== v) : [];
    if (cur.length === 0) delete n[k]; else n[k] = cur;
    return n;
  });
  const pushSingle = (k, value, countKey) => {
    chips.push({
      key: `${k}:${value.id ?? value.label}`,
      colLabel: COL_FILTER_LABELS[k] || k,
      valueLabel: value.label,
      count: getChipCount ? getChipCount(k, countKey ?? (value.id ?? value.label)) : null,
      onRemove: () => removeKey(k),
      testid: `docs-filter-chip-${k}`,
    });
  };
  const pushArr = (k, arr, labelFor) => {
    arr.forEach((v) => {
      const lbl = labelFor(v);
      if (!lbl) return;
      chips.push({
        key: `${k}:${v}`,
        colLabel: COL_FILTER_LABELS[k] || k,
        valueLabel: lbl,
        count: getChipCount ? getChipCount(k, v) : null,
        onRemove: () => removeArrValue(k, v),
        testid: `docs-filter-chip-${k}-${v}`,
      });
    });
  };

  if (colFilters.status) {
    const kpi = DOCS_KPIS.find((k) => k.key === colFilters.status);
    pushSingle("status", { label: kpi?.label || colFilters.status }, colFilters.status);
  }
  if (colFilters.type) {
    const arr = Array.isArray(colFilters.type) ? colFilters.type : [colFilters.type];
    pushArr("type", arr, (tid) => typesById?.[tid]?.name || null);
  }
  if (colFilters.project) {
    pushSingle("project", { label: projectsById?.[colFilters.project]?.name || "—" }, colFilters.project);
  }
  if (colFilters.creator) {
    const u = usersById?.[colFilters.creator];
    pushSingle("creator", { label: u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email : "—" }, colFilters.creator);
  }
  if (colFilters.resp) {
    const u = usersById?.[colFilters.resp];
    pushSingle("resp", { label: u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email : "—" }, colFilters.resp);
  }
  if (Array.isArray(colFilters.proEtat) && colFilters.proEtat.length > 0) {
    pushArr("proEtat", colFilters.proEtat, (v) => PRO_ETAT_LABEL[v] || v);
  }
  if (Array.isArray(colFilters.progress) && colFilters.progress.length > 0) {
    pushArr("progress", colFilters.progress, (v) => PROGRESS_LABEL[v] || v);
  }
  if (Array.isArray(colFilters.attachments) && colFilters.attachments.length > 0) {
    pushArr("attachments", colFilters.attachments, (v) => ATTACH_LABEL[v] || v);
  }

  if (chips.length === 0) return null;
  // Spec rev120 (2026-02) — bouton "Tout effacer" à la fin de la liste
  // dès que 3 puces ou plus sont actives. Un clic vide tous les
  // colFilters d'un coup (search/status principal préservés — ce
  // sont des filtres de haut niveau et non des colonnes).
  const clearAll = () => setColFilters({});
  return (
    <div className="flex items-center gap-1.5 flex-wrap shrink" data-testid="docs-active-filter-chips">
      {chips.map((c) => (
        <span
          key={c.key}
          className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap"
          style={{
            background: "#0A2540",
            color: "#6FA0E0",
            border: "1px solid rgba(111,160,224,0.28)",
          }}
          data-testid={c.testid}
        >
          <span style={{ color: "rgba(111,160,224,0.72)" }}>{c.colLabel}:</span>
          <span>{c.valueLabel}</span>
          {/* Spec rev139 (2026-02-06) — badge compteur : nombre de docs
              matchant ce filtre (facet count), entre le libellé et la
              croix. Fond bleu #0078D4, texte blanc, coins arrondis. */}
          {typeof c.count === "number" && (
            <span
              className="inline-flex items-center justify-center rounded-full text-[10px] font-bold tabular-nums"
              style={{
                minWidth: 18, height: 15, padding: "0 5px",
                background: "#0078D4", color: "#FFFFFF",
                lineHeight: 1,
              }}
              data-testid={`${c.testid}-count`}
              title={`${c.count} document${c.count > 1 ? "s" : ""} correspondant${c.count > 1 ? "s" : ""}`}
            >
              {c.count}
            </span>
          )}
          <button
            type="button"
            onClick={c.onRemove}
            className="inline-flex items-center justify-center rounded-full ml-0.5 hover:bg-white/10"
            style={{ width: 15, height: 15 }}
            aria-label={`Retirer le filtre ${c.colLabel}: ${c.valueLabel}`}
            title={`Retirer le filtre ${c.colLabel}: ${c.valueLabel}`}
            data-testid={`${c.testid}-remove`}
          >
            <X size={9} strokeWidth={2.8} />
          </button>
        </span>
      ))}
      {chips.length >= 3 && (
        <button
          type="button"
          onClick={clearAll}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium hover:bg-white/[0.06] whitespace-nowrap"
          style={{
            color: "#EF4444",
            border: "1px dashed rgba(239,68,68,0.45)",
          }}
          data-testid="docs-filter-chips-clear-all"
          title="Retirer tous les filtres de colonnes"
        >
          <X size={9} strokeWidth={2.8} />
          Tout effacer
        </button>
      )}
    </div>
  );
}

function QuickChip({ chip, active, onClick }) {
  const Ico = chip.Icon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`docs-chip-${chip.v}`}
      className="whitespace-nowrap inline-flex items-center gap-1 transition-colors flex-1 justify-center"
      style={{
        padding: "6px 10px",
        borderRadius: 15,
        fontSize: 11,
        fontWeight: active ? 600 : 400,
        // Spec 2026-07-18 (batch 5 points) #5 — Option B :
        // fond #26262A + bordure 1px #3A3A3F + texte #e8e8e5.
        border: `1px solid ${active ? `${chip.color}66` : "#3A3A3F"}`,
        background: active ? `${chip.color}22` : "#26262A",
        color: active ? chip.color : "#e8e8e5",
      }}
      title={active ? "Cliquez à nouveau pour désélectionner" : chip.l}
    >
      <Ico size={12} strokeWidth={2.2} style={{ color: chip.color, flexShrink: 0 }} />
      <span>{chip.l}</span>
      {/* Spec iter171 pt.8 (2026-02-27) — croix visible quand actif pour
          signifier explicitement qu'un re-clic désactive le filtre. */}
      {active && (
        <X size={10} strokeWidth={2.4} className="ml-0.5 opacity-80" />
      )}
    </button>
  );
}

function PeriodePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(value?.from || "");
  const [to, setTo] = useState(value?.to || "");

  useEffect(() => {
    setFrom(value?.from || "");
    setTo(value?.to || "");
  }, [value]);

  const todayIso = () => new Date().toISOString().slice(0, 10);
  const weekStartIso = () => {
    const d = new Date();
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); // Monday
    return d.toISOString().slice(0, 10);
  };
  const monthStartIso = () => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  };

  const preset = (label, from2, to2) => {
    onChange({ from: from2, to: to2, label });
    setOpen(false);
  };

  const applyCustom = () => {
    if (!from && !to) return;
    onChange({ from, to, label: `du ${from || "…"} au ${to || "…"}` });
    setOpen(false);
  };

  const on = !!value;
  const label = value?.label || "Période…";
  // Spec iter171 pt.8 (2026-02-27) — croix visible pour désactiver un
  // filtre rapide déjà appliqué (Période / Siège-Projet / etc.).
  const clear = (e) => { e.stopPropagation(); onChange(null); };

  return (
    <div className="relative" style={{ flex: 1 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="docs-chip-period"
        className="whitespace-nowrap inline-flex items-center justify-center gap-1 transition-colors w-full"
        style={{
          // Spec 2026-07-18 (batch 5 points) #5 — Option B :
          // fond #26262A + bordure 1px #3A3A3F + texte #e8e8e5.
          padding: "6px 10px",
          borderRadius: 15,
          fontSize: 11,
          fontWeight: on ? 600 : 400,
          border: `1px solid ${on ? "#F9731666" : "#3A3A3F"}`,
          background: on ? "#F9731622" : "#26262A",
          color: on ? "#F97316" : "#e8e8e5",
        }}
      >
        <CalendarRange size={12} strokeWidth={2.2} style={{ color: "#F97316", flexShrink: 0 }} />
        <span>{label}</span>
        {on ? (
          <span
            role="button"
            aria-label="Effacer le filtre période"
            tabIndex={0}
            onClick={clear}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); clear(e); } }}
            className="inline-flex items-center justify-center rounded-full ml-0.5 hover:bg-white/10"
            style={{ width: 14, height: 14 }}
            data-testid="docs-chip-period-clear"
            title="Effacer"
          >
            <X size={10} strokeWidth={2.4} />
          </span>
        ) : (
          <ChevronDown size={11} className="opacity-70" />
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div
            className="absolute left-0 mt-1 z-40 rounded-md border border-[#3A3A3A] bg-[#1E1E1E] py-2 shadow-lg"
            style={{ minWidth: 280 }}
            data-testid="docs-period-popover"
          >
            <div className="px-3 py-1 text-[9px] uppercase tracking-widest text-muted-foreground">Raccourcis</div>
            <button type="button" onClick={() => preset("Aujourd'hui", todayIso(), todayIso())} className="w-full text-left px-3 py-1.5 text-xs hover:bg-white/[0.06]">Aujourd&apos;hui</button>
            <button type="button" onClick={() => preset("Cette semaine", weekStartIso(), todayIso())} className="w-full text-left px-3 py-1.5 text-xs hover:bg-white/[0.06]">Cette semaine</button>
            <button type="button" onClick={() => preset("Ce mois", monthStartIso(), todayIso())} className="w-full text-left px-3 py-1.5 text-xs hover:bg-white/[0.06]">Ce mois</button>
            <div className="border-t border-white/10 my-1" />
            <div className="px-3 py-1 text-[9px] uppercase tracking-widest text-muted-foreground">Personnalisé</div>
            <div className="px-3 py-1.5 space-y-1.5">
              <div className="flex items-center gap-2 text-[11px]">
                <span className="w-8 text-muted-foreground">Du</span>
                <Input
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  className="h-8 flex-1"
                  style={{ background: "#1A1A1A", border: "1.5px solid rgba(255,255,255,0.08)", borderRadius: 7 }}
                />
              </div>
              <div className="flex items-center gap-2 text-[11px]">
                <span className="w-8 text-muted-foreground">Au</span>
                <Input
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className="h-8 flex-1"
                  style={{ background: "#1A1A1A", border: "1.5px solid rgba(255,255,255,0.08)", borderRadius: 7 }}
                />
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Button
                  size="sm"
                  onClick={applyCustom}
                  disabled={!from && !to}
                  className="flex-1 h-7 text-white border-0"
                  style={{ background: "#F97316" }}
                >Appliquer</Button>
                {on && (
                  <Button
                    size="sm" variant="outline"
                    onClick={() => { onChange(null); setOpen(false); }}
                    className="h-7 text-[11px]"
                  >Effacer</Button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function LocationChipDropdown({ projects, unassigned, value, onChange, nameById }) {
  const [open, setOpen] = useState(false);
  const on = !!value;
  const label = value === "__none__"
    ? "Sans siège / projet"
    : value
      ? (nameById[value] || "Siège / Projet")
      : "Siège / Projet";

  const select = (v) => {
    onChange(v);
    setOpen(false);
  };

  return (
    <div className="relative" style={{ flex: 1 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="docs-chip-location"
        className="whitespace-nowrap inline-flex items-center justify-center gap-1 transition-colors w-full"
        style={{
          padding: "6px 10px",
          borderRadius: 15,
          fontSize: 11,
          fontWeight: on ? 600 : 400,
          border: `1px solid ${on ? "#0078D466" : "#3A3A3F"}`,
          background: on ? "#0078D422" : "#26262A",
          color: on ? "#5BA3E3" : "#e8e8e5",
        }}
      >
        <MapPin size={12} strokeWidth={2.2} style={{ color: "#0078D4", flexShrink: 0 }} />
        <span>{label}</span>
        {on ? (
          <span
            role="button"
            aria-label="Effacer le filtre siège/projet"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onChange(null); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onChange(null); } }}
            className="inline-flex items-center justify-center rounded-full ml-0.5 hover:bg-white/10"
            style={{ width: 14, height: 14 }}
            data-testid="docs-chip-location-clear"
            title="Effacer"
          >
            <X size={10} strokeWidth={2.4} />
          </span>
        ) : (
          <ChevronDown size={11} className="opacity-70" />
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div
            className="absolute left-0 mt-1 z-40"
            style={{ ...PANEL, minWidth: 300, maxHeight: 360, overflowY: "auto" }}
            data-testid="docs-location-popover"
          >
            <button
              type="button"
              onClick={() => select("__none__")}
              className="w-full flex items-center text-left hover:bg-[#26262C]"
              style={{ height: 34, padding: "0 10px", borderRadius: 6, gap: 8, fontSize: 12.5, color: "#E8E8EA" }}
              data-testid="docs-location-none"
            >
              <span className="inline-flex justify-center" style={{ width: 18 }}><MapPinOff size={14} className="text-[#9CA3AF]" /></span>
              <span className="flex-1">Sans siège / projet</span>
              <span className="tabular-nums text-[10px] text-muted-foreground">{unassigned}</span>
            </button>
            <div style={{ height: 1, background: "#2A2A30", margin: "3px 0" }} />
            <J3Groups projects={projects} isSelected={(p) => p.id === value} onPick={(p) => select(p.id)} testIdPrefix="docs-location"
              rightOf={(p) => <span className="tabular-nums text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: "rgba(0,120,212,0.12)", color: "#5A9EE0" }}>{p.count}</span>} />
            {on && (
              <>
                <div style={{ height: 1, background: "#2A2A30", margin: "3px 0" }} />
                <button
                  type="button"
                  onClick={() => select("")}
                  className="w-full text-center px-3 py-1.5 text-[11px] text-muted-foreground hover:bg-white/[0.06]"
                >Effacer le filtre</button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
